import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { API_SERVICE_BACKEND } from "src/config";

const digitsOnly = (value) => value.replace(/[^0-9]/g, "");

const MIN_DELAY_FLOOR = 9;
const RANDOM_DELAY_FLOOR = 13;

const DelayConfigDialog = ({
  open,
  handleClose,
  acc,
  isBulkUpdate = false,
  selectedAccounts,
  setSelectedAccounts,
  fetchAllMailBoxes,
  userEmail,
}) => {
  const theme = useTheme();
  const [minDelayInMins, setMinDelayInMins] = useState("");
  const [delayEmailsInMins, setDelayEmailsInMins] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isBulkUpdate) {
      setMinDelayInMins("");
      setDelayEmailsInMins("");
      return;
    }
    setMinDelayInMins(
      acc?.mailboxSettings?.minDelayInMins != null
        ? String(acc.mailboxSettings.minDelayInMins)
        : ""
    );
    setDelayEmailsInMins(
      acc?.mailboxSettings?.delayEmailsInMins != null
        ? String(acc.mailboxSettings.delayEmailsInMins)
        : ""
    );
  }, [open, isBulkUpdate, acc]);

  const minValue = minDelayInMins === "" ? undefined : Number(minDelayInMins);
  const delayValue = delayEmailsInMins === "" ? undefined : Number(delayEmailsInMins);
  const minTooLow = minValue !== undefined && minValue < MIN_DELAY_FLOOR;
  const delayTooLow = delayValue !== undefined && delayValue < RANDOM_DELAY_FLOOR;
  const isValid =
    minValue !== undefined &&
    delayValue !== undefined &&
    !minTooLow &&
    !delayTooLow;

  const handleUpdate = async () => {
    const mailboxIds = isBulkUpdate ? selectedAccounts : [acc?._id].filter(Boolean);
    if (!mailboxIds?.length || !isValid) return;

    const payload = {
      mailboxIds,
      minDelayInMins: minValue,
      delayEmailsInMins: delayValue,
    };

    setLoading(true);
    try {
      const res = await axios.patch(
        `${API_SERVICE_BACKEND}/mailboxes/mailbox-settings`,
        payload
      );
      if (res?.data?.success) {
        toast.success("Delay settings updated successfully!");
        handleClose();
        if (isBulkUpdate) setSelectedAccounts?.([]);
        fetchAllMailBoxes?.(userEmail, true, null, true);
      } else {
        toast.error(res?.data?.message || "Failed to update delay settings");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update delay settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Delay Config</Typography>
        <IconButton onClick={handleClose}>
          <Close sx={{ color: theme.palette.text.primary }} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography sx={{ fontSize: "14px", color: theme.palette.text.secondary }}>
            Time gap between emails (per mailbox)
            {isBulkUpdate && selectedAccounts?.length
              ? ` — applies to ${selectedAccounts.length} selected mailbox(es)`
              : ""}
            :
          </Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Stack spacing={0.5}>
              <InputLabel sx={{ fontSize: "12px" }}>Min</InputLabel>
              <TextField
                size="small"
                type="number"
                value={minDelayInMins}
                onChange={(e) => setMinDelayInMins(digitsOnly(e.target.value))}
                error={minTooLow}
                className="glass-input"
                sx={{ width: "100px" }}
              />
            </Stack>
            <Stack spacing={0.5} sx={{ alignItems: "center" }}>
              <InputLabel sx={{ fontSize: "12px", visibility: "hidden" }}>+</InputLabel>
              <Box sx={{ height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ color: "text.primary" }}>+</Typography>
              </Box>
            </Stack>
            <Stack spacing={0.5}>
              <InputLabel sx={{ fontSize: "12px" }}>Random Delay</InputLabel>
              <TextField
                size="small"
                type="number"
                value={delayEmailsInMins}
                onChange={(e) => setDelayEmailsInMins(digitsOnly(e.target.value))}
                error={delayTooLow}
                className="glass-input"
                sx={{ width: "120px" }}
              />
            </Stack>
          </Stack>
          <Typography sx={{ fontSize: "12px", color: "error.main" }}>
            {minTooLow && `Min must be at least ${MIN_DELAY_FLOOR}. `}
            {delayTooLow && `Random Delay must be at least ${RANDOM_DELAY_FLOOR}. `}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button className="glass-button-secondary-50" onClick={handleClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={handleUpdate}
          loading={loading}
          disabled={!isValid}
        >
          Update Delay
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DelayConfigDialog;
